/**
 * What this client IS, for the `X-Apiboo-Client` request header.
 *
 * Contract (app <-> backend <-> mobile, PLAN 2026-08-23 §4):
 *
 *   X-Apiboo-Client: <kind>/<version> (<platform>)
 *     kind     = desktop | web | mobile | cli
 *     version  = ^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$   (<= 32 chars)
 *     platform = darwin | windows | linux | ios | android | web | unknown
 *     whole header <= 64 ASCII chars; anything else is treated as ABSENT
 *     server-side (no write, no error).
 *
 * The backend reads it on every authenticated call to keep `last_active_at`
 * and the last client version/platform per user (admin -> Users). It carries
 * nothing that identifies the install: the device id stays in `X-Client-Id`.
 *
 * Deliberately synchronous and dependency-free: it runs inside the axios
 * request interceptor of BOTH API instances, and `utils/tauri.js` statically
 * imports `@tauri-apps/api/core`, which this module must not pull into the
 * graph of everything that imports `clientId.js` (realtime hooks, sync
 * bridge, channel service). Runtime detection therefore mirrors the fallback
 * chain of `isTauri()` in `utils/tauri.js` — `window.isTauri` is the flag the
 * official `isTauri()` reads — without importing it.
 */
import packageJson from '../../package.json';

export const CLIENT_HEADER_NAME = 'X-Apiboo-Client';
export const CLIENT_HEADER_MAX_LENGTH = 64;
export const CLIENT_VERSION_MAX_LENGTH = 32;
export const FALLBACK_VERSION = '0.0.0';

const VERSION_RE = /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/;
const ASCII_RE = /^[\x20-\x7e]*$/;
const PLATFORMS = new Set(['darwin', 'windows', 'linux', 'ios', 'android', 'web', 'unknown']);

/** Build-time source of truth for the shipped version: `package.json`. */
function rawBuildVersion() {
  return packageJson && packageJson.version;
}

/**
 * The version exactly as the contract allows it, or `FALLBACK_VERSION`.
 * Never `undefined`, never `"undefined"`, never longer than the cap.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeClientVersion(raw) {
  const v = typeof raw === 'string' ? raw.trim() : '';
  if (!v || v.length > CLIENT_VERSION_MAX_LENGTH || !VERSION_RE.test(v)) {
    return FALLBACK_VERSION;
  }
  return v;
}

/** @returns {string} */
export function getClientVersion() {
  return normalizeClientVersion(rawBuildVersion());
}

/**
 * Are we inside the Tauri webview? Same signals `utils/tauri.js#isTauri`
 * checks, read synchronously and without the Tauri import.
 * @returns {boolean}
 */
export function isDesktopRuntime() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.isTauri === true) return true;
    if (window.__TAURI__ !== undefined || window.__TAURI_INTERNALS__ !== undefined) return true;
    if (window.location && window.location.protocol === 'tauri:') return true;
    if (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string') {
      return navigator.userAgent.includes('Tauri');
    }
  } catch {
    // a sandboxed window may refuse property access — treat as the web
  }
  return false;
}

/** @returns {'desktop' | 'web'} */
export function getClientKind() {
  return isDesktopRuntime() ? 'desktop' : 'web';
}

/**
 * OS of a desktop client from its user agent. Pure, so the mapping can be
 * tested as a table.
 * @param {string} userAgent
 * @returns {'darwin' | 'windows' | 'linux' | 'unknown'}
 */
export function platformFromUserAgent(userAgent) {
  const ua = typeof userAgent === 'string' ? userAgent : '';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS/i.test(ua)) return 'darwin';
  if (/Linux|X11/i.test(ua)) return 'linux';
  return 'unknown';
}

/**
 * `web` outside the desktop app (the OS of a browser visitor is not the
 * question the header answers); otherwise the OS from the user agent.
 * @returns {string}
 */
export function getClientPlatform() {
  if (!isDesktopRuntime()) return 'web';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return platformFromUserAgent(ua);
}

/**
 * Assemble the header value from its parts, enforcing the contract's caps.
 * A value the server would drop as malformed is replaced by the same kind and
 * platform with the fallback version — the activity row still gets written,
 * and a version string we cannot send is exactly what `0.0.0` means.
 * @param {{ kind: string, version: unknown, platform: string }} parts
 * @returns {string}
 */
export function formatClientHeader({ kind, version, platform }) {
  const k = kind === 'desktop' || kind === 'web' ? kind : 'web';
  const p = PLATFORMS.has(platform) ? platform : 'unknown';
  const v = normalizeClientVersion(version);
  const value = `${k}/${v} (${p})`;
  // With every token validated above the longest possible value is 50 chars;
  // the check stays as the contract's own statement of the limit.
  if (value.length <= CLIENT_HEADER_MAX_LENGTH && ASCII_RE.test(value)) return value;
  return `${k}/${FALLBACK_VERSION} (${p})`;
}

/** The `X-Apiboo-Client` value for THIS client, right now. */
export function buildClientHeader() {
  return formatClientHeader({
    kind: getClientKind(),
    version: rawBuildVersion(),
    platform: getClientPlatform(),
  });
}
